import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiServices from "../../Services/ApiServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faVideo,
  faBookOpen,
  faEye,
  faStar,
  faClock,
  faMoneyBill,
  faPercent,
  faLayerGroup,
  faBook,
  faCalculator,
  faUser,
  faSpinner,
  faCertificate,
  faEyeSlash,
  faDesktop,
  faGraduationCap,
  faHardDrive,
} from "@fortawesome/free-solid-svg-icons";
import { toastHelper } from "../../../Helpers/toastHelper";
import { getFullFileUrl } from "../../../Helpers/UrlHelpers";
import Loader from "../../Shared/Loader";
import CourseRequest from "./CourseRequest";
import CourseSubscription from "./CourseSubscription";
import Pagination from "../../Shared/Pagination";
import { motion, AnimatePresence } from "framer-motion";
import DeleteAlert from "../../Shared/DeleteAlert";
import extractErrorMessage from "../../../Helpers/APIHelper";
import DynamicModal from "../../Shared/DynamicModal";
import { useLanguage } from '../../../contexts/LanguageContext';
import SoftGrid from "../../Shared/SoftGrid/SoftGrid";
import ImagePreview from "../../Shared/ImagePreview/ImagePreview";
import ComponentPopup from "../../Shared/ComponentPopup";
import { encryptId } from "../../../utils/encryption";

const CourseManager = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("courses");
  const [totalCapacity, setTotalCapacity] = useState(null);

  // Add pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [recalculatingId, setRecalculatingId] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);

  const isEditing = Boolean(editingCourseId);

  // Live preview states
  const [previewCourseId, setPreviewCourseId] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'website' or 'student'

  // Update fetchCourses to handle pagination
  const fetchCourses = async (page = 1) => {
    setLoading(true);
    try {
      const res = await ApiServices.get(
        `Course/getAll?PageSize=${pageSize}&PageNumber=${page}&IsActive=true`
      );
      setCourses(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
      setCurrentPage(res.data.current_page || 1);
      const cap = res?.data?.totalUserCoursesCapacityInMB ?? res?.data?.total_user_courses_capacity_in_mb ?? res?.data?.TotalUserCoursesCapacityInMB ?? res?.data?.items?.[0]?.totalUserCoursesCapacityInMB ?? res?.data?.items?.[0]?.total_user_courses_capacity_in_mb ?? res?.data?.items?.[0]?.TotalUserCoursesCapacityInMB;
      if (cap !== undefined && cap !== null) {
        setTotalCapacity(cap);
      }
    } catch (e) {
      toastHelper.error(t('failedToLoadCourses'));
    }
    setLoading(false);
  };

  const formatCapacity = (val) => {
    if (val === null || val === undefined) return null;
    const num = Number(val);
    if (isNaN(num)) return null;
    if (num >= 1024) {
      return `${parseFloat((num / 1024).toFixed(2))} GB`;
    }
    return `${parseFloat(num.toFixed(2))} MB`;
  };

  const capacityFormatted = formatCapacity(totalCapacity ?? courses?.[0]?.totalUserCoursesCapacityInMB ?? courses?.[0]?.total_user_courses_capacity_in_mb ?? courses?.[0]?.TotalUserCoursesCapacityInMB);

  // Add pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchCourses(newPage);
    }
  };

  // Modify useEffect to fetch courses
  useEffect(() => {
    fetchCourses();
  }, []);

  // Handle edit
  const handleEdit = async (courseId) => {
    try {
      const res = await ApiServices.get(`Course/${courseId}`);
      if (res?.data) {
        setEditingCourseId(courseId);
        setEditingCourse(res.data);
        setShowCourseModal(true);
      }
    } catch (error) {
      toastHelper.error(t('failedToLoadCourseDetails'));
    }
  };

  const handleAddCourse = () => {
    setEditingCourseId(null);
    setEditingCourse(null);
    setShowCourseModal(true);
  };



  // Handle delete
  const handleDelete = (course) => {
    setSelectedCourse(course);
    setShowDeleteAlert(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCourse) return;

    setDeleteLoading(true);
    try {
      const response = await ApiServices.delete(
        `Course/delete/${selectedCourse.id}`
      );
      if (response.check) {
        toastHelper.success(t('courseDeletedSuccess'));
        fetchCourses(currentPage);
      }
    } catch (error) {
      toastHelper.error(t('courseDeleteFailed'));
    } finally {
      setDeleteLoading(false);
      setShowDeleteAlert(false);
      setSelectedCourse(null);
    }
  };

  const handleVideoClick = (courseId) => {
    navigate(`/teacher/courses/videos/${courseId}`);
  };

  const handleRecalculateHours = async (courseId) => {
    try {
      setRecalculatingId(courseId);
      const response = await ApiServices.put(
        `Course/ReCalculateCourseHour/${courseId}`
      );

      if (response.check) {
        toastHelper.success(t('courseHoursRecalculated'));
        fetchCourses(currentPage);
      } else {
        toastHelper.error(t('courseHoursRecalculateFailed'));
      }
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(
          error || error?.msg || t('courseHoursRecalculateError')
        );
      }
    } finally {
      setRecalculatingId(null);
    }
  };

  const handleToggleVisibility = async (courseId) => {
    try {
      setTogglingId(courseId);
      const response = await ApiServices.put(`Course/showOrHide/${courseId}`);
      if (response.check) {
        toastHelper.success(t('courseVisibilityChanged'));
        fetchCourses(currentPage);
      } else {
        toastHelper.error(t('courseVisibilityChangeFailed'));
      }
    } catch (error) {
      toastHelper.error(error?.message || t('courseVisibilityChangeError'));
    } finally {
      setTogglingId(null);
    }
  };

  const courseFormConfig = (courseData) => [
    {
      name: "Name",
      label: t('courseName'),
      type: "text",
      required: true,
      requiredMessage: t('courseNameRequired'),
      min: 3,
      max: 100,
      defaultValue: courseData?.name || "",
    },
    {
      name: "Description",
      label: t('courseDescription'),
      type: "styledText",
      required: true,
      requiredMessage: t('courseDescRequired'),
      min: 10,
      max: 50000,
      defaultValue: courseData?.description || "",
    },
    {
      name: "Price",
      label: t('price'),
      type: "number",
      required: true,
      requiredMessage: t('coursePriceRequired'),
      min: 0,
      defaultValue: courseData?.price ?? 0,
    },
    {
      name: "Discount",
      label: t('courseDiscountValue'),
      type: "number",
      min: 0,
      defaultValue: courseData?.discount ?? 0,
    },
    {
      name: "CategoryId",
      label: t('courseCategory'),
      type: "async-select",
      required: true,
      requiredMessage: t('courseCategoryRequired'),
      optionsUrl: "Lookup/Categories",
      optionsMap: (item) => ({ value: String(item.id), label: item.name }),
      valueLabel: courseData?.category,
      defaultValue: courseData?.category_id ? String(courseData.category_id) : "",
    },
    {
      name: "MaterialId",
      label: t('examMaterial'),
      type: "async-select",
      required: true,
      requiredMessage: t('examMaterialRequired'),
      optionsUrl: "Lookup/Materials",
      optionsMap: (item) => ({ value: String(item.id), label: item.name }),
      valueLabel: courseData?.materail || courseData?.material,
      defaultValue: courseData?.material_id ? String(courseData.material_id) : "",
    },
    {
      name: "Coupon",
      label: t('courseCouponOptional'),
      type: "text",
      defaultValue: courseData?.coupon || "",
    },
    {
      name: "IsCertified",
      label: t('courseIsCertified'),
      type: "select",
      required: true,
      defaultValue: String(courseData?.is_certified ? "true" : "false"),
      options: [
        { value: "true", label: t('examResetYes') },
        { value: "false", label: t('examResetNo') },
      ],
    },
    {
      name: "CourseCover",
      label: t('courseImage'),
      type: "file",
      accept: "image/*",
    },
  ];

  const handleCourseModalSubmit = async (values) => {
    const formData = new FormData();
    formData.append("Name", values.Name ?? "");
    formData.append("Description", values.Description ?? "");
    formData.append("Price", values.Price ?? 0);
    formData.append("Discount", values.Discount ?? 0);
    formData.append("CategoryId", values.CategoryId ?? "");
    formData.append("MaterialId", values.MaterialId ?? "");
    formData.append("Coupon", values.Coupon ?? "");
    formData.append("IsCertified", values.IsCertified === "true");

    if (values.CourseCover instanceof File) {
      formData.append("CourseCover", values.CourseCover, values.CourseCover.name);
    }

    if (isEditing) {
      await ApiServices.put(`Course/update/${editingCourseId}`, formData, true, 300000);
      toastHelper.success(t('courseUpdatedSuccess'));
    } else {
      await ApiServices.post("Course/create", formData, true, 300000);
      toastHelper.success(t('courseAddedSuccess'));
    }

    setShowCourseModal(false);
    setEditingCourseId(null);
    setEditingCourse(null);
    fetchCourses(currentPage);
  };

  return (
    <div className="course-manager">
      <div className="section-header">
        <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div className="title-section">
            <h2>{t('courseManagementTitle')}</h2>
            <span className="subtitle">{t('courseManagementSubtitle')}</span>
          </div>
          {capacityFormatted && (
            <div
              className="capacity-label-badge"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 14px',
                borderRadius: '20px',
                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                color: '#4f46e5',
                fontWeight: '600',
                fontSize: '0.875rem',
                border: '1px solid rgba(99, 102, 241, 0.2)',
              }}
            >
              <FontAwesomeIcon icon={faHardDrive} />
              <span>
                {t('totalUserCoursesCapacityInMB') || (language === 'ar' ? 'إجمالي السعة' : 'Total Capacity')}: {capacityFormatted}
              </span>
            </div>
          )}
        </div>
        <div className="header-right">
          {activeTab === "courses" && (
            <button
              className="add-course-btn"
              onClick={handleAddCourse}
            >
              <FontAwesomeIcon icon={faPlus} />{t('addNewCourse')}</button>
          )}
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <motion.button
            className={`tab-btn ${activeTab === "courses" ? "active" : ""}`}
            onClick={() => setActiveTab("courses")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FontAwesomeIcon icon={faBookOpen} />{t('courses')}</motion.button>
          <motion.button
            className={`tab-btn ${activeTab === "subscriptions" ? "active" : ""
              }`}
            onClick={() => setActiveTab("subscriptions")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FontAwesomeIcon icon={faUser} />{t('subscriptionsTab')}</motion.button>
          <motion.button
            className={`tab-btn ${activeTab === "requests" ? "active" : ""}`}
            onClick={() => setActiveTab("requests")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FontAwesomeIcon icon={faUser} />{t('registrationRequestsTab')}</motion.button>
        </div>
      </div>

      {activeTab === "courses" ? (
        <div className="courses-container" style={{ marginTop: '2rem' }}>
          <SoftGrid
            columns={[
              {
                key: "course_cover",
                label: t('courseImage'),
                render: (value, row) => (
                  <ImagePreview
                    src={value ? getFullFileUrl(value) : null}
                    alt={row.name}
                    style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
                    fallbackIcon={<FontAwesomeIcon icon={faBookOpen} />}
                  />
                )
              },
              {
                key: "name",
                label: t('courseName'),
                sortable: true,
                render: (value, row) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: language === 'ar' ? 'right' : 'left' }}>
                    <span style={{ fontWeight: 600 }}>{value}</span>
                    {row.is_certified && (
                      <span style={{ fontSize: '0.75rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FontAwesomeIcon icon={faCertificate} /> {t('certified')}
                      </span>
                    )}
                  </div>
                )
              },
              { key: "category", label: t('courseCategory'), sortable: true },
              { key: "material_name", label: t('examMaterial'), sortable: true },
              {
                key: "price",
                label: t('price'),
                sortable: true,
                render: (value) => `${value} ${t('currencyEGP')}`
              },
              { key: "discount", label: t('courseDiscountValue'), sortable: true },
              {
                key: "views",
                label: <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{t('views')}</div>,
                sortable: true
              },
              {
                key: "rate",
                label: <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{t('rate')}</div>,
                sortable: true
              },
              {
                key: "hours",
                label: <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{t('hours')}</div>,
                sortable: true
              },
            ]}
            data={courses}
            isLoading={loading}
            isPaginated={true}
            pageNumber={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            emptyStateMessage={t('noCoursesYet')}
            actions={(row) => [
              {
                label: t('courseEdit') || 'Edit',
                icon: <FontAwesomeIcon icon={faEdit} />,
                onClick: () => handleEdit(row.id),
                className: 'edit'
              },
              {
                label: t('delete') || 'Delete',
                icon: <FontAwesomeIcon icon={faTrash} />,
                onClick: () => handleDelete(row),
                className: 'delete'
              },
              {
                label: t('videos') || 'Videos',
                icon: <FontAwesomeIcon icon={faVideo} />,
                onClick: () => handleVideoClick(row.id),
              },
              {
                label: row.is_hidden ? t('showCourse') : t('hideCourse'),
                icon: togglingId === row.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={row.is_hidden ? faEyeSlash : faEye} />,
                onClick: () => handleToggleVisibility(row.id),
                disabled: togglingId === row.id,
                className: row.is_hidden ? 'warning' : ''
              },
              {
                label: t('recalculateHours') || 'Recalculate',
                icon: recalculatingId === row.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCalculator} />,
                onClick: () => handleRecalculateHours(row.id),
                disabled: recalculatingId === row.id
              },
              {
                label: t('websitePreview') || 'معاينة الموقع',
                icon: <FontAwesomeIcon icon={faDesktop} />,
                onClick: () => { setPreviewCourseId(row.id); setPreviewType('website'); },
              }
            ]}
          />
        </div>
      ) : activeTab === "subscriptions" ? (
        <CourseSubscription />
      ) : (
        <CourseRequest />
      )}

      <DeleteAlert
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setSelectedCourse(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDeleteCourseTitle')}
        message={t('confirmDeleteCourseMessage')}
        itemName={selectedCourse?.name}
        itemDescription={`${t('materialLabelPrefix')}${selectedCourse?.materail}`}
        loading={deleteLoading}
      />

      <DynamicModal
        isOpen={showCourseModal}
        onClose={() => {
          setShowCourseModal(false);
          setEditingCourseId(null);
          setEditingCourse(null);
        }}
        title={isEditing ? t('courseEdit') : t('createNewCourseTitle')}
        submitButtonText={isEditing ? t('courseUpdate') : t('createCourseBtn')}
        formConfig={courseFormConfig(editingCourse)}
        multipart={true}
        direction={language === 'ar' ? 'rtl' : 'ltr'}
        onSubmit={handleCourseModalSubmit}
        onError={(error) => {
          const title = error?.title || error?.message || "";
          if (title.toLowerCase().includes("validation errors")) {
            const errMessage = extractErrorMessage(error);
            toastHelper.error(errMessage);
          } else {
            toastHelper.error(error || error?.msg || t('courseSaveError'));
          }
        }}
      />

      {/* Website Live Preview */}
      <ComponentPopup
        isOpen={previewType === 'website' && previewCourseId !== null}
        onClose={() => { setPreviewCourseId(null); setPreviewType(null); }}
        title={t('websitePreview') || 'معاينة الموقع'}
        subtitle={t('livePreviewSubtitle') || 'معاينة مباشرة - للعرض فقط'}
        icon={faDesktop}
        enableLivePreview={true}
        maxWidth="95vw"
        maxHeight="95vh"
        previewUrl={`/course/${encryptId(previewCourseId) || previewCourseId}?preview=true`}
      />
      <style jsx>{`
        .course-manager {
          padding: 2rem;
          background: #fff;
          min-height: 100vh;
          border-radius: 30px;
        }


        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding: 24px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
        }


        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-left {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .title-section {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .section-icon {
          font-size: 2rem;
          color: #6366f1;
        }

        h2 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .subtitle {
          color: #64748b;
          font-size: 1rem;
        }

        .add-course-btn {
          background: #6366f1;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          font-size: 1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
            font-family: "Zain", sans-serif;
        }

        .add-course-btn:hover {
          background: #4338ca;
        }

        .tabs-container {
          background: white;
          border-radius: 1rem;
          padding: 1rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .tabs {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .tab-btn {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          color: #64748b;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
            font-family: "Zain", sans-serif;
          white-space: nowrap;
        }

        .tab-btn:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .tab-btn.active {
          background: #6366f1;
          color: white;
            font-family: "Zain", sans-serif;
        }



        .courses-container {
          margin-top: 2rem;
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .no-courses {
          text-align: center;
          padding: 4rem;
          background: white;
          border-radius: 1rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .no-courses svg {
          font-size: 3rem;
          color: #64748b;
          margin-bottom: 1rem;
        }

        .no-courses h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 0.5rem;
        }

        .no-courses p {
          color: #64748b;
          margin: 0;
        }

        .courses-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 2rem;
        }

        .course-card {
          background: white;
          border-radius: 1rem;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .course-cover {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .course-cover img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .no-image {
          width: 100%;
          height: 100%;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
        }

        .no-image svg {
          font-size: 3rem;
        }

        .course-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .course-card:hover .course-overlay {
          opacity: 1;
        }

        .course-actions {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: white;
          color: #1e293b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .action-btn:hover {
          transform: scale(1.1);
        }

        .action-btn.edit:hover {
          background: #4f46e5;
          color: white;
        }

        .action-btn.delete:hover {
          background: #ef4444;
          color: white;
        }

        .action-btn.video:hover {
          background: #10b981;
          color: white;
        }

        .action-btn.toggle-visibility:hover {
          background: #6366f1;
          color: white;
        }

        .action-btn.toggle-visibility.hidden {
          color: #ef4444;
        }

        .action-btn.toggle-visibility.visible {
          color: #10b981;
        }
        
        .action-btn.toggle-visibility:hover.hidden,
        .action-btn.toggle-visibility:hover.visible {
          color: white;
        }

        .course-content {
          padding: 1.5rem;
        }

        .course-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
          flex-wrap: wrap;
        }

        .course-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          flex: 1;
        }

        .certified-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 0.75rem;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
          transition: all 0.3s ease;
        }

        .certified-badge.certified {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .certified-badge.not-certified {
          background: #e5e7eb;
          color: #6b7280;
          border: 1px solid #d1d5db;
        }

        .certified-badge svg {
          font-size: 0.875rem;
        }

        .course-description {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0 0 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .course-meta {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          background: #f8fafc;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          color: #1e293b;
        }

        .meta-item svg {
          color: #4f46e5;
        }

        .recalculate-btn {
          width: 100%;
          padding: 0.75rem;
          border: none;
          border-radius: 0.5rem;
          background: #f1f5f9;
          color: #1e293b;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.2s;
            font-family: "Zain", sans-serif;
        }

        .recalculate-btn:hover {
          background: #e2e8f0;
        }

        .recalculate-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 2rem;
        }

        .page-btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 0.5rem;
          background: white;
          color: #1e293b;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .page-btn:hover:not(:disabled) {
          background: #f1f5f9;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-info {
          color: #64748b;
          font-size: 0.875rem;
        }

        @media (max-width: 1024px) {
          .course-manager {
            padding: 1rem;
          }
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 1rem;
          }

          .add-course-btn {
            width: 100%;
            justify-content: center;
          }

          .tabs {
            flex-direction: column;
          }

          .tab-btn {
            width: 100%;
            justify-content: center;
          }

          .courses-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseManager;
